import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";

const IMPACT_COLORS: Record<string, string> = {
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  low: "bg-green-500/15 text-green-400 border-green-500/30",
};

export default function RegulationDetail() {
  const { slug } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["regulation", slug],
    queryFn: async () => (await api.get(`/regulations/${slug}`)).data,
  });

  const showLoader = useMinLoading(isLoading, 700, slug);
  if (showLoader) return <PageLoader />;
  if (!data?.regulation) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="font-heading text-2xl text-white mb-3">Regulation not found</h1>
      <Link to="/regulation-tracker" className="text-[#7C3AED] hover:underline">Back to tracker</Link>
    </div>;
  }
  const reg = data.regulation;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="regulation-detail-page">
      <Seo title={reg.title} description={reg.summary || reg.title} path={`/regulation/${reg.slug}`} />
      <Link to="/regulation-tracker" className="font-mono text-xs text-[#7C3AED] hover:underline">← Regulation Tracker</Link>
      <div className="flex flex-wrap gap-2 mt-4 mb-4">
        <Badge variant="outline" className={IMPACT_COLORS[reg.impact_level] || IMPACT_COLORS.medium}>
          {reg.impact_level} impact
        </Badge>
        <Badge variant="outline" className="border-white/20 text-gray-300 font-mono text-[10px] uppercase">
          {reg.status.replace("_", " ")}
        </Badge>
      </div>
      <h1 className="font-heading font-black text-white text-2xl sm:text-4xl leading-tight">{reg.title}</h1>
      <p className="font-mono text-xs text-gray-500 mt-4 uppercase tracking-wide">
        {reg.countries.join(", ")} {reg.agencies.length > 0 && `· ${reg.agencies.join(", ")}`}
        {reg.effective_date && ` · Effective ${reg.effective_date}`}
      </p>

      <div className="mt-8 text-gray-300 leading-relaxed text-base sm:text-lg">{reg.summary}</div>

      {reg.why_it_matters && (
        <div className="mt-8 bg-[#0d0d14] border border-[#7C3AED]/30 rounded-lg p-6" data-testid="why-it-matters">
          <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm uppercase tracking-wide mb-3">
            <Landmark className="h-4 w-4 text-[#7C3AED]" /> Why it matters
          </h3>
          <p className="text-gray-300 leading-relaxed">{reg.why_it_matters}</p>
        </div>
      )}

      {(reg.timeline || []).length > 0 && (
        <div className="mt-10" data-testid="regulation-timeline">
          <h3 className="font-heading font-bold text-white text-lg mb-6">Timeline</h3>
          <div className="border-l-2 border-[#7C3AED]/30 pl-6 space-y-6">
            {reg.timeline.map((t: any, i: number) => (
              <div key={i} className="relative">
                <span className="absolute -left-[31px] top-1 h-2.5 w-2.5 rounded-full bg-[#7C3AED]" />
                <p className="font-mono text-xs text-[#7C3AED]">{t.date}</p>
                <p className="text-gray-300 mt-1">{t.event}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(reg.topics || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-10">
          {reg.topics.map((t: string) => (
            <span key={t} className="font-mono text-xs text-gray-400 border border-white/10 px-3 py-1 rounded">#{t}</span>
          ))}
        </div>
      )}

      {(data.related_articles || []).length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading font-bold text-white text-xl mb-6">Related Coverage</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {data.related_articles.map((a: Article) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}
    </div>
  );
}
